import z from "zod/v4";

export const SessionStatusList = [
  "active",
  "waiting_for_input",
  "waiting_for_tool_approval",
  "processing",
  "completed",
  "continue",
  "paused",
  "error",
  "timeout",
] as const;

export const SessionStatusSchema = z.enum(SessionStatusList);
export type SessionStatus = z.infer<typeof SessionStatusSchema>;

export const WorkflowSessionSchema = z.object({
  sessionId: z.string(),
  workflowId: z.string(),
  version: z.string(),
  orgId: z.string(),
  userId: z.string(),
  currentNodeId: z.string(),
  nextNodeId: z.string(),
  status: SessionStatusSchema,
  contextS3Key: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()),
  createdAt: z.number(),
  updatedAt: z.number(),
});

export type WorkflowSession = z.infer<typeof WorkflowSessionSchema>;

export type CreateWorkflowSession = Omit<
  WorkflowSession,
  "orgId" | "userId" | "createdAt" | "updatedAt"
>;

/**
 * How long a session marked `processing` is treated as still running a turn.
 *
 * A turn is marked `processing` when it starts and settled when it ends, and
 * the server keeps running a turn after its browser disconnects. So while the
 * mark is fresh, a second input on the same session would start an
 * overlapping run, and a page that reloaded mid-turn should wait for the
 * running one instead. A process that dies mid-turn never settles its mark;
 * past this window the mark is stale and the session takes input again.
 * Comfortably longer than one turn (a node times out after 300s by default).
 */
export const TURN_IN_PROGRESS_STALE_MS = 15 * 60 * 1000;

export const isTurnInProgress = (
  session: Pick<WorkflowSession, "status" | "updatedAt">,
  now: number
): boolean =>
  session.status === "processing" &&
  now - session.updatedAt < TURN_IN_PROGRESS_STALE_MS;
